// @flow

import React, {type Node} from 'react';
import {connect} from 'react-redux';

import type {
  ModelChangeMessageType,
  ModelSubscribedComponentPropsType,
} from 'app/subscriptions';
import {fetchNotificationsList} from 'app/notifly/actions';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {whenVisible} from 'app/utils/page-visibility';
import type {NotificationType} from 'app/notifly/types';
import {runWhenAnyModelMatch} from 'app/subscriptions';
import {getNotificationsByContentType} from 'app/notifly/utils';
import {type contentType} from 'app/utils/content-type';
import type {StoreShapeType} from 'app/reducers/types';

type OwnProps = {|
  contentType: contentType,
  documentsIds: Array<number>,
  children: ({[string]: Array<NotificationType>}) => Node,
|};

type StateProps = {|
  notifications: {[contentType]: {[string]: Array<NotificationType>}},
|};

type DispatchProps = {|
  fetchNotificationsList: (params?: {[string]: any}) => Promise<any>,
|};

type ConnectedProps = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

type Props = {|
  ...ConnectedProps,
  ...ModelSubscribedComponentPropsType,
|};

export class BaseDocumentsNotificationsProvider extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.props.onModelChange(this.handleModelChange);
  }

  componentDidMount() {
    this.fetchNotifications(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const {documentsIds} = this.props;

    // compare arrays signatures
    if (documentsIds.join(':') !== nextProps.documentsIds.join(':')) {
      this.fetchNotifications(nextProps);
    }
  }

  fetchNotifications = whenVisible(({documentsIds}: Props) => {
    if (documentsIds.length) {
      this.fetchNotificationsForDocument(documentsIds);
    }
  });

  fetchNotificationsForDocument = whenVisible((id?: number | Array<number>) => {
    this.props.fetchNotificationsList({
      is_all: false,
      target_object_id: Array.isArray(id) ? id.join(',') : id,
      target_content_type: this.props.contentType,
    });
  });

  handleModelChange = (message: ModelChangeMessageType) => {
    const {documentsIds} = this.props;

    if (documentsIds.length === 0) {
      return;
    }

    runWhenAnyModelMatch(message, 'notification', () =>
      this.fetchNotifications(this.props)
    );
  };

  render() {
    const {children, notifications, contentType} = this.props;
    return children(notifications[contentType] || {});
  }
}

const mapStateToProps = (state, {contentType, documentsIds}): StateProps => ({
  notifications: getNotificationsByContentType(
    state,
    contentType,
    documentsIds
  ),
});

// $FlowFixMe
const mapDispatchToProps: DispatchProps = {fetchNotificationsList};

export const DocumentsNotificationsProvider = connect<
  ConnectedProps,
  OwnProps,
  StateProps,
  DispatchProps,
  StoreShapeType,
  _
>(
  mapStateToProps,
  mapDispatchToProps
)(keepUpdatedEnhancer<ConnectedProps>(BaseDocumentsNotificationsProvider));
